/*!
 * mm-pair.js — «which of my songs go together?». The person drops a handful of their own songs;
 * each is analysed ONCE (tempo on the middle minute, key on the whole song) and every pair is
 * ranked by the SAME verdict the decks print (MashupPlan.pairFit). One press puts a pair into the
 * decks. Owns its panel only; strings, the per-list cap and the way into the decks come from the page.
 *
 *   var F = MashupPair.create({ els, t, feature, ctx: fn → AudioContext, cap, paid, plan, ctaUrl, onPick(fileA, fileB, info) });
 *   F.add(files)   — more songs (deduplicated; past the cap the rest is named, not analysed)
 *   F.stop()       — no more analysis (the decks need the beat model now)
 *
 * Cost: the beat model is the price — ~1 minute a whole song on WASM, seconds on a graphics card
 * (tool memory, 23.09) — so it hears the middle minute, where a song keeps its tempo; the key needs
 * the whole song and costs little (a chroma pass in the same worker). Songs are decoded one at a
 * time and dropped after their numbers: a list of fifty never holds more than one song in memory.
 */
(function (root) {
    'use strict';

    var EXCERPT = 60;           // seconds of the song the beat model hears
    var SHOW = 8;               // pairs on screen before «show all»

    function create(o) {
        var els = o.els, t = o.t, feature = o.feature || function () {};
        var songs = [], running = false, stopped = false, gen = 0, showAll = false, capNote = '';

        function keyOf(f) { return [f.name, f.size, f.lastModified].join('|'); }
        function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
        function nameOf(f) { return String(f && f.name || '').replace(/\.[^.]+$/, ''); }
        function camHue(cam) { var n = parseInt(cam, 10); return isFinite(n) ? ((n - 1) * 30 + 200) % 360 : 215; }
        function fmtPct(x) {
            try { return new Intl.NumberFormat(root.__LOCALE || 'en', { style: 'percent', maximumFractionDigits: 0, numberingSystem: 'latn' }).format(x); } catch (_) { return Math.round(x * 100) + '%'; }
        }

        function excerpt(buf) {
            var d = buf.duration;
            if (d <= EXCERPT + 15) return buf;
            var sr = buf.sampleRate, a = Math.floor((d / 2 - EXCERPT / 2) * sr), n = Math.floor(EXCERPT * sr);
            var out;
            try { out = new AudioBuffer({ numberOfChannels: buf.numberOfChannels, length: n, sampleRate: sr }); }
            catch (_) { out = o.ctx().createBuffer(buf.numberOfChannels, n, sr); }
            for (var c = 0; c < buf.numberOfChannels; c++) out.getChannelData(c).set(buf.getChannelData(c).subarray(a, a + n));
            return out;
        }

        async function analyze(s) {
            var AD = root.AudioDecoderRobust, An = root.MashupAnalysis, t0 = performance.now();
            var r = await AD.robust(s.file, o.ctx(), { slug: 'mashup-maker' });
            var buf = r && r.buffer ? r.buffer : r;
            if (!buf || !buf.length) throw new Error('empty decode');
            s.dur = buf.duration;
            // the LOWEST priority of the analysis scheduler: a song a person put in a deck is listened to
            // first, the finder takes what is left (it used to queue between a deck's beats and its key)
            var J = An.job ? An.job({ prio: 2 }) : An;
            var grid = await J.beats(excerpt(buf), {});
            var mix = await J.mixFeat(buf);
            buf = null;
            s.bpm = grid && grid.bpm > 0 ? grid.bpm : 0;
            s.meter = grid && grid.meter || 4;
            s.key = mix && mix.key || null;
            s.ok = s.bpm > 0 && (grid.confidence == null || grid.confidence >= 0.35);
            s.state = s.ok ? 'done' : 'nobeat';
            feature('pair_song', { ms: Math.round(performance.now() - t0), sec: Math.round(s.dur), bpm: s.bpm ? Math.round(s.bpm * 10) / 10 : null, ok: s.ok ? 1 : 0, conf: grid ? Math.round((grid.confidence || 0) * 100) / 100 : null });
        }

        function paintSongs() {
            els.songs.innerHTML = songs.map(function (s, i) {
                var meta = s.state === 'done' ? '<b dir="ltr">' + Math.round(s.bpm) + ' BPM</b>' + (s.key ? ' <span class="mm-ps-key" style="--cam-h:' + camHue(s.key.camelot) + '" dir="ltr">' + esc(s.key.name) + ' · ' + esc(s.key.camelot) + '</span>' : '')
                    : s.state === 'nobeat' ? '<span class="mm-ps-bad">' + esc(t('pair_no_beat')) + '</span>'
                    : s.state === 'failed' ? '<span class="mm-ps-bad">' + esc(t('pair_failed')) + '</span>'
                    : s.state === 'busy' ? '<span class="mm-ps-busy">' + esc(t('analyzing')) + '</span>' : '<span class="mm-ps-wait">…</span>';
                return '<li class="mm-ps is-' + s.state + '"><span class="mm-ps-n">' + esc(nameOf(s.file)) + '</span><span class="mm-ps-m">' + meta + '</span></li>';
            }).join('');
            els.songs.hidden = !songs.length;
            els.clear.hidden = !songs.length || running;
        }

        function pairs() {
            var P = root.MashupPlan, ok = songs.filter(function (s) { return s.state === 'done'; }), out = [];
            for (var i = 0; i < ok.length; i++) for (var j = i + 1; j < ok.length; j++) {
                var a = ok[i], b = ok[j];
                out.push({ a: a, b: b, fit: P.pairFit({ bpm: a.bpm, meter: a.meter, key: a.key }, { bpm: b.bpm, meter: b.meter, key: b.key }) });
            }
            out.sort(function (x, y) { return x.fit.score - y.fit.score; });
            return out;
        }

        function paintPairs() {
            var list = pairs(), shown = showAll ? list : list.slice(0, SHOW);
            els.list.innerHTML = shown.map(function (p, r) {
                var f = p.fit, keys = p.a.key && p.b.key ? ' · <span dir="ltr">' + esc(p.a.key.name) + ' / ' + esc(p.b.key.name) + '</span>' : '';
                var tempo = '<span dir="ltr">' + Math.round(p.a.bpm) + ' / ' + Math.round(p.b.bpm) + ' BPM</span>' + (f.pct >= 0.015 ? ' · ' + esc(t('pair_stretch', { pct: fmtPct(f.pct) })) : '');
                return '<li class="mm-pr ' + f.verdict + '">'
                    + '<span class="mm-verdict ' + f.verdict + '">' + esc(t('fit_' + f.verdict)) + '</span>'
                    + '<span class="mm-pr-body"><span class="mm-pr-names"><b>' + esc(nameOf(p.a.file)) + '</b> × <b>' + esc(nameOf(p.b.file)) + '</b></span>'
                    + '<span class="mm-pr-facts">' + tempo + keys + '</span></span>'
                    + '<button type="button" class="tc-btn ghost sm" data-pr="' + r + '" aria-label="' + esc(t('pair_go_aria', { a: nameOf(p.a.file), b: nameOf(p.b.file) })) + '">' + esc(t('pair_go')) + '</button></li>';
            }).join('');
            els.list.hidden = !shown.length;
            els.list.__pairs = shown;
            els.more.hidden = showAll || list.length <= SHOW;
            if (!els.more.hidden) els.more.textContent = t('pair_more', { n: list.length });
            els.swapNote.hidden = !shown.length;
            return list;
        }

        function status(text) { els.status.textContent = text || ''; els.status.hidden = !text; }

        async function pump() {
            if (running) return;
            running = true; stopped = false;
            var my = ++gen, t0 = performance.now(), n0 = songs.filter(function (s) { return s.state === 'wait'; }).length;
            if (n0) feature('pair_start', { n: n0, total: songs.length, cap: o.cap });
            try {
                for (var i = 0; i < songs.length; i++) {
                    var s = songs[i];
                    if (s.state !== 'wait') continue;
                    if (stopped || my !== gen) break;
                    s.state = 'busy'; paintSongs();
                    var done = songs.filter(function (x) { return x.state !== 'wait' && x.state !== 'busy'; }).length;
                    status(t('pair_status', { i: done + 1, n: songs.length, name: nameOf(s.file) }));
                    try { await analyze(s); }
                    catch (e) {
                        // the listening was called off (the build took the workers), not failed: the song
                        // waits for the next run of the finder instead of being marked unreadable
                        if (e && e.name === 'AbortError') { s.state = 'wait'; stopped = true; break; }
                        // a graphics card that answered wrong: the song goes again, once, on WASM
                        if (e && e.code === 'model_switched' && !s.retried) { s.retried = true; s.state = 'wait'; i--; continue; }
                        s.state = 'failed';
                        try { root.__aeDiagnostic && root.__aeDiagnostic({ issue: 'pair_analyze_failed', err: String(e && e.message || e).slice(0, 120) }); } catch (_) {}
                    }
                    if (my !== gen) return;
                    paintSongs(); paintPairs();
                }
            } finally {
                if (my === gen) {
                    running = false; paintSongs();
                    var list = paintPairs(), good = 0, ok = 0, hard = 0;
                    list.forEach(function (p) { if (p.fit.verdict === 'good') good++; else if (p.fit.verdict === 'ok') ok++; else hard++; });
                    var usable = songs.filter(function (s) { return s.state === 'done'; }).length;
                    if (!stopped) {
                        status((usable < 2 ? t('pair_none') : t('pair_done', { n: usable, p: list.length })) + (capNote ? ' ' + capNote : ''));
                        if (n0) feature('pair_done', { n: songs.length, usable: usable, pairs: list.length, good: good, ok: ok, hard: hard, ms: Math.round(performance.now() - t0) });
                    }
                }
            }
        }

        function add(files) {
            var have = {}, n = 0, skipped = 0;
            songs.forEach(function (s) { have[s.k] = 1; });
            (files || []).forEach(function (f) {
                var k = keyOf(f);
                if (have[k]) return;
                if (songs.length >= o.cap) { skipped++; return; }
                have[k] = 1; n++;
                songs.push({ file: f, k: k, state: 'wait' });
            });
            capNote = '';
            if (skipped) {
                capNote = t('pair_cap_hit', { cap: o.cap, n: songs.length + skipped, plan: o.plan || '', paid: o.paid });
                feature('pair_cap_hit', { n: songs.length + skipped, cap: o.cap, paid: o.cap >= o.paid ? 1 : 0 });
                paintCap(true);
            }
            paintSongs();
            // a pump stopped by a pick but still finishing its song picks the new ones up (it re-reads the list)
            if (n) { stopped = false; pump(); }
            else if (capNote) status(capNote);
        }
        function paintCap(hit) {
            var more = o.cap < o.paid && o.plan;
            els.cap.innerHTML = esc(t('pair_cap', { cap: o.cap })) + (more ? ' ' + esc(t('pair_cap_paid', { plan: o.plan, paid: o.paid })) + (o.ctaUrl ? ' <a href="' + esc(o.ctaUrl) + '">' + esc(t('pair_cta', { plan: o.plan })) + '</a>' : '') : '');
            els.cap.classList.toggle('warn', !!hit);
        }

        els.list.addEventListener('click', function (e) {
            var b = e.target.closest('[data-pr]'); if (!b) return;
            var p = (els.list.__pairs || [])[parseInt(b.dataset.pr, 10)]; if (!p) return;
            stop();
            feature('pair_pick', { rank: parseInt(b.dataset.pr, 10) + 1, verdict: p.fit.verdict, pct: Math.round(p.fit.pct * 1000) / 10, rel: p.fit.rel, n: songs.length });
            o.onPick(p.a.file, p.b.file, { verdict: p.fit.verdict });
        });
        els.more.addEventListener('click', function () { showAll = true; paintPairs(); feature('pair_more', { n: songs.length }); });
        els.clear.addEventListener('click', function () {
            gen++; running = false; stopped = true; songs = []; showAll = false; capNote = '';
            paintSongs(); paintPairs(); status(''); paintCap(false);
            try { if (root.ToolUploader && root.ToolUploader.clear) root.ToolUploader.clear(els.zone.id); } catch (_) {}
        });
        // the song in the worker finishes (a minute on WASM at worst); the rest waits — more files
        // added later resume from them
        function stop() {
            if (!running) return;
            stopped = true;
            status('');
        }
        paintCap(false);
        return { add: add, stop: stop, songs: function () { return songs; }, pairs: pairs, running: function () { return running; } };
    }

    root.MashupPair = { create: create, EXCERPT: EXCERPT };
})(typeof window !== 'undefined' ? window : self);
